import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const Kbd: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <kbd className="px-1.5 py-0.5 text-xs font-mono bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded border border-gray-300 dark:border-gray-600">
    {children}
  </kbd>
);

/**
 * Global keyboard shortcuts:
 *   /  focus the customer search input (on home)
 *   g h  go home
 *   g s  go to support
 *   g t  go to templates
 *   ?  show the shortcut overlay
 *   esc closes the overlay
 *
 * The handler ignores presses when an editable element is focused, so users
 * typing into inputs/textareas/contenteditable fields aren't surprised.
 */
const KeyboardShortcuts: React.FC = () => {
  const [showHelp, setShowHelp] = useState(false);
  const navigate = useNavigate();

  // "g <letter>" chord support — remember the pending leader key for ~1s.
  const [chord, setChord] = useState<string | null>(null);

  const focusSearch = useCallback(() => {
    const el = document.querySelector(
      'input[type="search"], input[placeholder*="earch" i]'
    ) as HTMLInputElement | null;
    if (el) {
      el.focus();
      el.select?.();
    }
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Don't intercept when user is editing text
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      const editable =
        tag === 'input' ||
        tag === 'textarea' ||
        tag === 'select' ||
        target?.isContentEditable;

      // Esc closes overlay even from inputs
      if (e.key === 'Escape' && showHelp) {
        setShowHelp(false);
        return;
      }

      if (editable) return;

      // Ignore when modifier keys are held (don't fight browser shortcuts)
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      if (e.key === '?') {
        e.preventDefault();
        setShowHelp((v) => !v);
        return;
      }

      if (e.key === '/') {
        e.preventDefault();
        focusSearch();
        return;
      }

      if (chord === 'g') {
        if (e.key === 'h') { navigate('/'); setChord(null); return; }
        if (e.key === 's') { navigate('/support'); setChord(null); return; }
        if (e.key === 't') { navigate('/prompt-templates'); setChord(null); return; }
        setChord(null);
      }

      if (e.key === 'g') {
        setChord('g');
        // Auto-clear so a stray 'g' doesn't linger
        window.setTimeout(() => setChord((c) => (c === 'g' ? null : c)), 1200);
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [chord, navigate, focusSearch, showHelp]);

  if (!showHelp) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4"
      onClick={() => setShowHelp(false)}
      role="dialog"
      aria-labelledby="shortcuts-title"
    >
      <div
        className="bg-white dark:bg-dark-surface rounded-lg shadow-xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 id="shortcuts-title" className="text-lg font-semibold text-secondary dark:text-dark-text">
            Keyboard shortcuts
          </h3>
          <button
            onClick={() => setShowHelp(false)}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <dl className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <dt className="text-gray-700 dark:text-gray-300">Global search</dt>
            <dd className="flex gap-1"><Kbd>⌘</Kbd><Kbd>K</Kbd></dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-gray-700 dark:text-gray-300">Focus customer search</dt>
            <dd><Kbd>/</Kbd></dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-gray-700 dark:text-gray-300">Go home</dt>
            <dd className="flex gap-1"><Kbd>g</Kbd><Kbd>h</Kbd></dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-gray-700 dark:text-gray-300">Go to Support</dt>
            <dd className="flex gap-1"><Kbd>g</Kbd><Kbd>s</Kbd></dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-gray-700 dark:text-gray-300">Go to Templates</dt>
            <dd className="flex gap-1"><Kbd>g</Kbd><Kbd>t</Kbd></dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-gray-700 dark:text-gray-300">Show this help</dt>
            <dd><Kbd>?</Kbd></dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-gray-700 dark:text-gray-300">Close dialogs</dt>
            <dd><Kbd>Esc</Kbd></dd>
          </div>
        </dl>
      </div>
    </div>
  );
};

export default KeyboardShortcuts;
